import { Box, InputBase, Typography } from '@mui/material'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'

import { Button } from '@/apps/latest/components/atoms/Button'
import { PillSelect } from '@/apps/latest/components/molecules/PillSelect'
import { fileToAttachment } from '@/apps/latest/features/attachments/fileToAttachment'
import {
  ComposerNote,
  CounselComposer,
  isSendKey,
  type MicState,
} from '@/apps/latest/features/counsel/CounselComposer'
import { CounselLine } from '@/apps/latest/features/counsel/CounselLine'
import {
  CounselTracePane,
  type TurnInsight,
} from '@/apps/latest/features/counsel/CounselTracePane'
import {
  CounselSeat,
  type SeatState,
} from '@/apps/latest/features/counsel/CounselSeat'
import {
  useAutoSpeak,
  useSpeech,
} from '@/apps/latest/features/counsel/useSpeech'
import { useVoiceInput } from '@/apps/latest/features/counsel/useVoiceInput'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { fieldInputSx } from '@/apps/latest/shared/styles/inlineInput'
import { BodyText } from '@/components/atoms/BodyText'
import { SectionHeading } from '@/components/atoms/SectionHeading'
import {
  COUNSEL_FEEDBACK_MAX_CHARS,
  COUNSEL_TEXT_MAX_CHARS,
  counselErrorMessage,
  getCounselCases,
  postCounselFinish,
  postCounselTranscribe,
  postCounselTurn,
  type CounselCasesResponse,
  type CounselSource,
  type CounselSummary,
} from '@/features/counsel'
import { LAYOUT, SPACING } from '@/shared/styles/spacing'
import { UI_FONT_SIZE } from '@/shared/styles/typography'

interface Line {
  speaker: 'nagi' | 'user'
  text: string
  sources?: CounselSource[]
  time?: string
  // 一緒に送った写真の名前。写真そのものは画面にも back にも残さない
  attachment?: string
  // nagi の返事の行だけ。その回の見立てと門 (広い画面の右の列が読む)
  insight?: TurnInsight
}

const MIC_FAILED = 'マイクを使えません。ブラウザでマイクを許可してください。'

const clock = () =>
  new Date().toLocaleTimeString('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
  })

/**
 * がんの相談チャット (/counsel/:token)。認証なしで開ける試作の 1 枚。
 * 形は executive-rag の相談の面を写す (design-book 04-apps/kakeru.md がんの相談・ADR-0068)。
 *
 * - 上 = 試作だけの「相談する人」の切替と場の前提、その下に nagi の席 (上端に貼り付く)。
 * - 中 = 会話の行。nagi の行は右端に読み上げ、調べた返事は `参照した資料` の開閉を持つ。
 * - 下 = [写真] [欄] [mic] の 1 行と `終える`。終えると同じページの下に要点と感想の欄が出る
 *   (試作だけが要点を相談者にも見せるので、本来は薬剤師にだけ届くと 1 行で書く)。
 * - 会話の状態は back が持つ (session_id)。画面の行の位置 = back の会話の位置 (最初の一言が 0)。
 *   読み上げはこの位置で行を指す。
 * - 広い画面 (lg 以上) だけ、右端に高さいっぱいの見立ての trace の面 (試作。ADR-0091)。スマホ幅の面は変えない。
 */
const CounselPage = () => {
  const { token = '' } = useParams<{ token: string }>()
  const [setup, setSetup] = useState<CounselCasesResponse | null>(null)
  const [isUnavailable, setIsUnavailable] = useState(false)
  const [caseKey, setCaseKey] = useState('')
  const [lines, setLines] = useState<Line[]>([])
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [text, setText] = useState('')
  const [pending, setPending] = useState<'reply' | 'summary' | null>(null)
  const [micState, setMicState] = useState<MicState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [summary, setSummary] = useState<CounselSummary | null>(null)
  const [feedback, setFeedback] = useState('')
  const [sentFeedback, setSentFeedback] = useState<string | null>(null)
  const ringRef = useRef<HTMLSpanElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const speech = useSpeech(token, ringRef)
  const [isAutoSpeak, flipAutoSpeak] = useAutoSpeak()
  const voice = useVoiceInput()

  useEffect(() => {
    getCounselCases(token)
      .then((data) => {
        setSetup(data)
        setCaseKey(data.cases[0]?.key ?? '')
        setLines([{ speaker: 'nagi', text: data.opening }])
      })
      .catch(() => setIsUnavailable(true))
  }, [token])

  // 開いた時・ケースを替えた時 (最初の一言だけ) は送らない。上のケースと前提が見えるまま始める
  useEffect(() => {
    if (lines.length <= 1 && !pending && !summary) return
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [lines.length, pending, summary])

  // ケースを替えたら会話を最初からにする (前の会話は back に残る)
  const handleCaseChange = (key: string) => {
    if (!setup) return
    speech.hush()
    setCaseKey(key)
    setLines([{ speaker: 'nagi', text: setup.opening }])
    setSessionId(null)
    setText('')
    setError(null)
    setSummary(null)
    setFeedback('')
    setSentFeedback(null)
  }

  // 自動で読み上げが入りなら、送る操作 (押す・打つ) の中で音の器を起こしておく。
  // 返事が届くのは数秒後で、そのときは操作の外 (iOS の Safari は操作の外で作った器を鳴らさない)
  const wakeIfAuto = () => {
    if (isAutoSpeak) speech.wake()
  }

  const send = async (said: string, photo?: File) => {
    if ((!said && !photo) || pending) return
    let attachment: Blob | undefined
    if (photo) {
      try {
        attachment = await fileToAttachment(photo)
      } catch (e) {
        setError(e instanceof Error ? e.message : '写真を送れませんでした。')
        return
      }
    }
    speech.hush()
    const userIndex = lines.length
    setLines((prev) => [
      ...prev,
      {
        speaker: 'user',
        text: said,
        time: clock(),
        attachment: photo?.name,
      },
    ])
    setText('')
    setError(null)
    setPending('reply')
    const startedAt = performance.now()
    try {
      const turn = await postCounselTurn(
        token,
        {
          ...(sessionId ? { session_id: sessionId } : { case_key: caseKey }),
          text: said,
        },
        attachment
      )
      setSessionId(turn.session_id)
      setLines((prev) => [
        ...prev,
        {
          speaker: 'nagi',
          text: turn.reply,
          sources: turn.sources,
          insight: {
            ...turn,
            said: said || '（写真）',
            seconds: (performance.now() - startedAt) / 1000,
          },
        },
      ])
      if (isAutoSpeak) speech.speak(userIndex + 1, turn.session_id)
    } catch (e) {
      // back は失敗した発言を残さない。画面からも外し、打ち直さずに送り直せるよう欄に戻す
      setLines((prev) => prev.slice(0, userIndex))
      setText(said)
      setError(counselErrorMessage(e))
    } finally {
      setPending(null)
    }
  }

  const handleSend = () => {
    wakeIfAuto()
    void send(text.trim())
  }

  const handlePhoto = (file: File) => {
    wakeIfAuto()
    void send(text.trim(), file)
  }

  // 押す → 話す → 押す。書き起こした文はそのまま送る (聞き違いは送った行で見える)
  const handleMic = async () => {
    wakeIfAuto()
    setError(null)
    if (micState === 'idle') {
      try {
        await voice.start()
        setMicState('recording')
      } catch {
        setError(MIC_FAILED)
      }
      return
    }
    if (micState !== 'recording') return
    setMicState('busy')
    try {
      const heard = await postCounselTranscribe(token, await voice.stop())
      setMicState('idle')
      await send(heard)
    } catch (e) {
      setMicState('idle')
      setError(counselErrorMessage(e))
    }
  }

  const finish = async () => {
    if (!sessionId || pending) return
    speech.hush()
    setError(null)
    setPending('summary')
    try {
      const result = await postCounselFinish(token, { session_id: sessionId })
      setSummary(result.summary)
    } catch (e) {
      setError(counselErrorMessage(e))
    } finally {
      setPending(null)
    }
  }

  const sendFeedback = async () => {
    const said = feedback.trim()
    if (!sessionId || !said || pending) return
    setError(null)
    try {
      await postCounselFinish(token, { session_id: sessionId, feedback: said })
      setSentFeedback(said)
    } catch (e) {
      setError(counselErrorMessage(e))
    }
  }

  if (isUnavailable) {
    return (
      <Page>
        <BodyText sx={{ color: colorTokens.fg.muted }}>
          このページは開けません。
        </BodyText>
      </Page>
    )
  }
  if (!setup) return null

  const current = setup.cases.find((c) => c.key === caseKey)
  const hasUserLine = lines.some((line) => line.speaker === 'user')
  const seatState: SeatState =
    speech.speaking?.phase === 'playing'
      ? 'speaking'
      : pending || speech.speaking
      ? 'busy'
      : 'idle'
  const note = error ?? speech.error
  const insights = lines.flatMap((line) => (line.insight ? [line.insight] : []))

  return (
    <Page aside={<CounselTracePane insights={insights} />}>
      <Box sx={{ pt: { xs: 2, sm: 4 } }}>
        <PillSelect
          value={caseKey}
          options={setup.cases.map((c) => ({
            value: c.key,
            label: c.label,
          }))}
          onChange={handleCaseChange}
          ariaLabel="相談する人"
          disabled={!!pending}
        />
        {current && (
          <Typography
            sx={{
              mt: 0.5,
              px: 1.25,
              fontSize: UI_FONT_SIZE.label,
              lineHeight: 1.5,
              color: colorTokens.fg.muted,
            }}
          >
            {current.premise}
          </Typography>
        )}
      </Box>

      <CounselSeat
        state={seatState}
        ringRef={ringRef}
        isAutoSpeak={isAutoSpeak}
        onToggleAutoSpeak={() => {
          if (isAutoSpeak) speech.hush()
          else speech.wake()
          flipAutoSpeak()
        }}
      />

      <Box sx={{ mt: 1 }}>
        {lines.map((line, i) =>
          line.speaker === 'user' ? (
            <CounselLine
              key={i}
              speaker="あなた"
              text={line.text}
              time={line.time}
              attachment={line.attachment}
            />
          ) : (
            <CounselLine
              key={i}
              speaker="nagi"
              text={line.text}
              sources={line.sources}
              speech={{
                phase:
                  speech.speaking?.index === i
                    ? speech.speaking.phase
                    : undefined,
                onToggle: () => speech.toggle(i, sessionId),
              }}
            />
          )
        )}
        {pending && (
          <CounselLine
            speaker="nagi"
            pending={pending === 'reply' ? '返事を作成中' : '要点を作成中'}
          />
        )}
      </Box>

      {summary ? (
        <Box sx={{ mt: 5 }}>
          <ComposerNote text={note} />
          <Typography
            sx={{
              mb: 2,
              fontSize: UI_FONT_SIZE.tag,
              lineHeight: 1.6,
              color: colorTokens.fg.muted,
            }}
          >
            ここから下は、薬剤師に届く要点です。相談する人の画面には出ません（試作のため表示しています）。
          </Typography>
          <SummaryBlock summary={summary} />
          <SectionHeading sx={{ mt: 3, mb: 0.5 }}>感想</SectionHeading>
          {sentFeedback !== null ? (
            <BodyText sx={{ whiteSpace: 'pre-wrap' }}>{sentFeedback}</BodyText>
          ) : (
            <InputBase
              multiline
              minRows={2}
              maxRows={8}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              onKeyDown={(e) => {
                if (!isSendKey(e)) return
                e.preventDefault()
                void sendFeedback()
              }}
              // 見出しと同じ語にすると、薄い字が見出しの写しに見えて欄と読めない
              placeholder="話してみた感想を書く（Enter で送る）"
              inputProps={{
                'aria-label': '感想',
                maxLength: COUNSEL_FEEDBACK_MAX_CHARS,
                enterKeyHint: 'send',
              }}
              sx={{ ...fieldInputSx, width: '100%' }}
            />
          )}
        </Box>
      ) : (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            mt: 1,
            pt: 1,
            pb: 2,
            backgroundColor: colorTokens.bg.default,
          }}
        >
          <CounselComposer
            value={text}
            onChange={setText}
            onSend={handleSend}
            onPhoto={handlePhoto}
            micState={micState}
            onMic={() => void handleMic()}
            maxLength={COUNSEL_TEXT_MAX_CHARS}
            isBusy={!!pending}
          />
          <ComposerNote text={note} />
          {hasUserLine && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mt: 1,
              }}
            >
              <Button
                tone="ghost"
                size="sm"
                disabled={!!pending || micState !== 'idle'}
                onClick={finish}
              >
                終える
              </Button>
            </Box>
          )}
        </Box>
      )}
      {/* 送る先はページの末尾。下端に貼り付く入力の箱の中に置くと、箱がいつも画面の中にあり送られない */}
      <div ref={bottomRef} />
    </Page>
  )
}

// 見立ての面の幅 (design-book ADR-0091)
const TRACE_PANE_WIDTH = 440

// 本文は中央 720px。スマホ幅は外周 16px。lg 以上だけ右端に高さいっぱいの aside の面を足し、本文は残りの幅の中央
const Page = ({
  children,
  aside,
}: {
  children: ReactNode
  aside?: ReactNode
}) => (
  <Box
    sx={(theme) => ({
      minHeight: '100dvh',
      backgroundColor: colorTokens.bg.default,
      // 面の枠は高さいっぱいに伸ばす (中の面が上端に貼り付くため)
      ...(aside && {
        [theme.breakpoints.up('lg')]: {
          display: 'grid',
          gridTemplateColumns: `minmax(0, 1fr) ${TRACE_PANE_WIDTH}px`,
        },
      }),
    })}
  >
    <Box
      sx={{
        minWidth: 0,
        px: SPACING.pageOuter,
      }}
    >
      <Box
        sx={{
          mx: 'auto',
          maxWidth: LAYOUT.contentMax,
        }}
      >
        {children}
      </Box>
    </Box>
    {aside && <Box sx={{ display: { xs: 'none', lg: 'block' } }}>{aside}</Box>}
  </Box>
)

// 要点。下川先生が見立ての当否を言えるよう、試作では薬剤師向けの項も画面に出す (log/259 work-log/01 §5.2)
const SummaryBlock = ({ summary }: { summary: CounselSummary }) => {
  const items: [string, string][] = [
    ['要旨', summary.gist],
    ['その人について', summary.person],
    ['分かったこと', summary.facts],
    ['向き合っていること', summary.step],
    ['心のゆとり', summary.room],
    ['体', summary.body],
    ['つなぐ先', summary.handoff],
    ['薬剤師へ', summary.for_pharmacist],
  ]
  return (
    <>
      {items.map(([label, value]) => (
        <Box key={label} sx={{ mb: 2 }}>
          <SectionHeading sx={{ mb: 0.5 }}>{label}</SectionHeading>
          <BodyText sx={{ whiteSpace: 'pre-wrap' }}>{value}</BodyText>
        </Box>
      ))}
    </>
  )
}

export default CounselPage
